import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getLabel } from "scmp-app/components/content/content-item-render/variants/multimedia/helpers";
import type { multimediaV2ContentItemContent$key } from "scmp-app/queries/__generated__/multimediaV2ContentItemContent.graphql";

import { Container, Content, CoverImage, Headline, Label, StyledEntityLink } from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: multimediaV2ContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment multimediaV2ContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...helpersGetLabelContent
        ...hooksContentItemProvidersContent
          @arguments(
            withHeadline: true
            withCoverImage: true
            withCoverImageSize540x360: true
            withCoverImageSize1200x800: true
            withVideoDurationOverlay: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(content);

  const label = getLabel(content);

  return (
    <Container className={className} ref={ref}>
      <StyledEntityLink
        onClick={() => onClick?.(content.entityId, content.urlAlias)}
        reference={content}
      >
        <Content>
          <Label>{label}</Label>
          <Headline>
            {providers.headline({
              hideTag: true,
              hideTooltip: true,
              preferSocialHeadline: true,
              skipHeadlineSponsorStyle: true,
            })}
          </Headline>
        </Content>
        <CoverImage>
          {providers.coverImage({
            responsiveVariants: {
              desktopUp: "size540x360",
              mobileUp: "size1200x800",
              tabletUp: "size540x360",
            },
            withCaption: false,
          })}
          {providers.video?.durationOverlay({
            iconSize: "hidden",
            variant: "homepage",
          })}
        </CoverImage>
      </StyledEntityLink>
    </Container>
  );
};

Component.displayName = "ContentItemMultimediaV2";

export const ContentItemMultimediaV2 = withHighlightedHeadline(Component);
